import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, Settings2, Ticket, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { countryName } from '@/i18n';
import { formatDate } from '@/lib/format';
import { useSession } from '@/api/session';
import { uploadDocument } from '@/api/hooks/uploads';
import {
  useDeleteVignette,
  useSaveVignette,
  useSetVignetteCountries,
  useVignetteHistory,
  useVignettes,
  type Vignette,
} from '@/api/hooks/vignettes';
import { Badge } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { DatePicker, Field, Input } from '@/components/ui/Field';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { toast } from '@/stores/toast';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ВИНЕТКИ — бус × държава
 *
 *  Годишни винетки само там, където бусовете ходят (сега България, Румъния,
 *  Унгария, Австрия). Зелено — валидна; жълто — изтича до две седмици;
 *  червено — изтекла или липсва. Клик върху клетка — подновяване.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function VignettesPage() {
  const role = useSession((st) => st.user?.role);
  const canEdit = role === 'owner' || role === 'dispatcher';
  const { data, isLoading, isError, refetch } = useVignettes();
  const [cell, setCell] = useState<{ vehicleId: string; label: string; country: string; current: Vignette | null } | null>(null);
  const [editingCountries, setEditingCountries] = useState(false);

  if (isLoading) return <LoadingBlock />;
  if (isError || !data) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <div className="space-y-3 p-3 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
            <Ticket className="size-5 text-ink-dim" />
            Винетки
          </h1>
          <p className="text-sm text-ink-dim">Годишните винетки по бусове — само за държавите, през които минават.</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
          {data.summary.missing ? <Badge tone="danger">липсват: {data.summary.missing}</Badge> : null}
          {data.summary.expired ? <Badge tone="danger">изтекли: {data.summary.expired}</Badge> : null}
          {data.summary.expiring ? <Badge tone="warn">изтичат до {data.warnDays} дни: {data.summary.expiring}</Badge> : null}
          {!data.summary.missing && !data.summary.expired && !data.summary.expiring ? <Badge tone="ok">всичко е валидно</Badge> : null}
          {role === 'owner' ? (
            <Button size="sm" variant="secondary" icon={<Settings2 className="size-3.5" />} onClick={() => setEditingCountries(true)}>
              Държави
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="overflow-hidden">
        {data.vehicles.length === 0 ? (
          <EmptyState title="Няма бусове." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH>Бус</TH>
                  {data.countries.map((c) => (
                    <TH key={c}>{countryName(c)}</TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {data.vehicles.map((van) => {
                  const idle = van.status !== 'active' && van.status !== 'in_service';
                  return (
                    <TR key={van.id} className={idle ? 'opacity-50' : undefined}>
                      <TD className="whitespace-nowrap">
                        <Link to="/vehicles/$vehicleId" params={{ vehicleId: van.id }} className="font-display font-semibold hover:text-accent">
                          {van.fleetNo}
                        </Link>
                        <span className="ml-2 font-mono text-xs text-ink-dim">{van.plate}</span>
                      </TD>
                      {data.countries.map((country) => {
                        const v = van.cells[country] ?? null;
                        return (
                          <TD key={country}>
                            <button
                              type="button"
                              disabled={!canEdit && !v}
                              onClick={() => setCell({ vehicleId: van.id, label: `${van.fleetNo} · ${van.plate}`, country, current: v })}
                              className={cn(
                                'w-full rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                                !v
                                  ? 'border-danger/40 bg-danger-soft/40 text-danger'
                                  : v.status === 'expired'
                                    ? 'border-danger/40 bg-danger-soft/40 text-danger'
                                    : v.status === 'expiring'
                                      ? 'border-warn/40 bg-warn-soft/40 text-warn'
                                      : 'border-ok/30 bg-ok-soft/30 text-ink hover:border-ok/60',
                              )}
                            >
                              {!v ? (
                                'няма — добави'
                              ) : (
                                <>
                                  <span className="font-mono">до {formatDate(v.validUntil)}</span>
                                  <span className="block text-[10px] opacity-80">
                                    {v.status === 'expired' ? `изтекла преди ${-v.daysLeft} дни` : `още ${v.daysLeft} дни`}
                                  </span>
                                </>
                              )}
                            </button>
                          </TD>
                        );
                      })}
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      {cell ? <VignetteDialog cell={cell} canEdit={canEdit} onClose={() => setCell(null)} /> : null}
      {editingCountries ? <CountriesDialog current={data.countries} onClose={() => setEditingCountries(false)} /> : null}
    </div>
  );
}

/**
 * Годишната винетка не е „от днес до след година" навсякъде. Предложението се
 * вижда в полето и се поправя по квитанцията.
 */
function suggestUntil(country: string, from: string): string {
  const d = new Date(`${from}T00:00:00Z`);
  const year = d.getUTCFullYear();
  /**
   * Унгария („éves") и Австрия: годишната е за календарна година и важи до 31
   * януари на следващата; от 1 декември се купува тази за новата година.
   */
  if (country === 'HU' || country === 'AT') return d.getUTCMonth() === 11 ? `${year + 2}-01-31` : `${year + 1}-01-31`;
  const end = new Date(Date.UTC(year + 1, d.getUTCMonth(), d.getUTCDate() - 1));
  return end.toISOString().slice(0, 10); // 12 месеца (България, Румъния)
}

function addDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function VignetteDialog({
  cell,
  canEdit,
  onClose,
}: {
  cell: { vehicleId: string; label: string; country: string; current: Vignette | null };
  canEdit: boolean;
  onClose: () => void;
}) {
  const save = useSaveVignette();
  const remove = useDeleteVignette();
  const history = useVignetteHistory(cell.vehicleId);
  const today = new Date().toISOString().slice(0, 10);
  /** Подновяване: новата започва деня след старата (ако старата още не е изтекла). */
  const startFrom = cell.current && cell.current.validUntil >= today ? addDay(cell.current.validUntil) : today;
  const [mode, setMode] = useState<'new' | 'edit'>('new');
  const [form, setForm] = useState({
    validFrom: startFrom,
    validUntil: suggestUntil(cell.country, startFrom),
    reference: '',
    amount: '',
    notes: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const past = (history.data ?? []).filter((v) => v.country === cell.country);

  const startEdit = (v: Vignette) => {
    setMode('edit');
    setForm({
      validFrom: v.validFrom ?? '',
      validUntil: v.validUntil,
      reference: v.reference ?? '',
      amount: v.amount != null ? String(v.amount).replace('.', ',') : '',
      notes: v.notes ?? '',
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      /** Квитанцията (снимка или PDF) отива като документ на буса. */
      const doc = file ? await uploadDocument(file, { type: 'vignette', vehicleId: cell.vehicleId, notes: `Винетка ${cell.country}` }) : null;
      const amount = form.amount.trim() ? Number(form.amount.replace(/\s/g, '').replace(',', '.')) : null;
      await save.mutateAsync({
        ...(mode === 'edit' && cell.current ? { id: cell.current.id } : {}),
        body: {
          vehicleId: cell.vehicleId,
          country: cell.country,
          validFrom: form.validFrom || null,
          validUntil: form.validUntil,
          reference: form.reference.trim() || null,
          amount: amount !== null && Number.isFinite(amount) ? amount : null,
          ccy: 'EUR',
          ...(doc ? { documentId: doc.id } : {}),
          notes: form.notes.trim() || null,
        },
      });
      toast.ok(mode === 'edit' ? 'Винетката е поправена' : 'Винетката е записана', `${countryName(cell.country)} до ${formatDate(form.validUntil)}`);
      onClose();
    } catch (error) {
      toast.error('Винетката не е записана', error instanceof Error ? error.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width="lg"
      title={`Винетка ${countryName(cell.country)} — бус ${cell.label}`}
      description={
        cell.current
          ? `Текуща: до ${formatDate(cell.current.validUntil)}${cell.current.reference ? ` · № ${cell.current.reference}` : ''}`
          : 'Още няма винетка за тази държава.'
      }
      footer={
        canEdit ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Отказ
            </Button>
            <Button variant="primary" loading={busy} disabled={!form.validUntil} onClick={() => void submit()}>
              {mode === 'edit' ? 'Запиши поправката' : cell.current ? 'Запиши новата' : 'Запиши'}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Затвори
          </Button>
        )
      }
    >
      <div className="space-y-4">
        {canEdit ? (
          <div className="space-y-3">
            {cell.current ? (
              <div className="flex gap-1.5 text-xs">
                <Button size="sm" variant={mode === 'new' ? 'primary' : 'secondary'} icon={<Plus className="size-3.5" />} onClick={() => {
                  setMode('new');
                  setForm({ validFrom: startFrom, validUntil: suggestUntil(cell.country, startFrom), reference: '', amount: '', notes: '' });
                }}>
                  Нова (подновяване)
                </Button>
                <Button size="sm" variant={mode === 'edit' ? 'primary' : 'secondary'} onClick={() => startEdit(cell.current!)}>
                  Поправи текущата
                </Button>
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Валидна от">
                <DatePicker
                  withTime={false}
                  value={form.validFrom}
                  onChange={(e) => setForm({ ...form, validFrom: e.target.value, ...(mode === 'new' && e.target.value ? { validUntil: suggestUntil(cell.country, e.target.value) } : {}) })}
                />
              </Field>
              <Field label="Валидна до" required hint="Предложението е за годишна винетка — сверете с квитанцията.">
                <DatePicker withTime={false} value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
              </Field>
              <Field label="№ на винетката / квитанцията">
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </Field>
              <Field label="Цена (€)">
                <Input inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </Field>
              <Field label="Квитанция (снимка или PDF)" className="sm:col-span-2">
                <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </Field>
              <Field label="Бележка" className="sm:col-span-2">
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
          </div>
        ) : null}

        {past.length > 0 ? (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-faint">История</div>
            <ul className="divide-y divide-line rounded-lg border border-line text-sm">
              {past.map((v) => (
                <li key={v.id} className="flex items-center gap-2 px-3 py-1.5">
                  <span className="font-mono text-xs">
                    {v.validFrom ? `${formatDate(v.validFrom)} – ` : 'до '}
                    {formatDate(v.validUntil)}
                  </span>
                  {v.reference ? <span className="text-xs text-ink-dim">№ {v.reference}</span> : null}
                  {v.amount != null ? <span className="text-xs text-ink-dim">{v.amount.toLocaleString('bg-BG')} {v.ccy ?? '€'}</span> : null}
                  <Badge tone={v.status === 'expired' ? 'neutral' : v.status === 'expiring' ? 'warn' : 'ok'} className="ml-auto">
                    {v.status === 'expired' ? 'изтекла' : v.status === 'expiring' ? 'изтича' : 'валидна'}
                  </Badge>
                  {canEdit ? (
                    <IconButton label="Изтрий" onClick={() => window.confirm('Да изтрия ли тази винетка?') && remove.mutate(v.id)}>
                      <Trash2 className="size-3.5" />
                    </IconButton>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

const ALL_COUNTRIES = ['BG', 'RO', 'HU', 'AT', 'SK', 'CZ', 'SI', 'CH', 'DE', 'IT'];

function CountriesDialog({ current, onClose }: { current: string[]; onClose: () => void }) {
  const set = useSetVignetteCountries();
  const [chosen, setChosen] = useState<string[]>(current);
  const toggle = (c: string) => setChosen(chosen.includes(c) ? chosen.filter((x) => x !== c) : [...chosen, c]);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Държави с годишна винетка"
      description="Само тези излизат в таблицата и в „Аномалии“."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button
            variant="primary"
            loading={set.isPending}
            disabled={chosen.length === 0}
            onClick={() =>
              set.mutate(chosen, {
                onSuccess: () => {
                  toast.ok('Държавите са записани');
                  onClose();
                },
              })
            }
          >
            Запиши
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap gap-1.5">
        {ALL_COUNTRIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => toggle(c)}
            className={cn(
              'rounded-full border px-3 py-1 text-sm',
              chosen.includes(c) ? 'border-accent bg-accent-soft text-accent' : 'border-line-strong text-ink-dim hover:text-ink',
            )}
          >
            {countryName(c)}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
